import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from "react";
import styles from "./formSecurity.module.css";

// Public reCAPTCHA v2 site keys (safe to ship; the secret lives only on the API).
// The key is chosen by hostname, not build mode, because the live site may be
// served by the development server. Google's test key is used only on
// localhost, where the kurr.com key would fail its domain check.
const KUR_SITE_KEY = "6LeC2-EtAAAAALhAzuO3hPkTMHEGpmJb2isCCjW-";
const GOOGLE_TEST_SITE_KEY = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";
const IS_LOCAL = ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);
const SITE_KEY = KUR_SITE_KEY;
const SCRIPT_ID = "google-recaptcha-v2";
const ONLOAD = "__kurRecaptchaReady";

let scriptPromise;
const loadRecaptcha = () => {
  if (window.grecaptcha && window.grecaptcha.render) return Promise.resolve(window.grecaptcha);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      window[ONLOAD] = () => resolve(window.grecaptcha);
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = `https://www.google.com/recaptcha/api.js?onload=${ONLOAD}&render=explicit`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        scriptPromise = undefined;
        reject(new Error("reCAPTCHA failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

// reCAPTCHA v2 checkbox plus a hidden honeypot field. The token is written
// into the form through setValue so the form schema can require it.
// Call ref.reset() after every submit attempt: tokens are single use.
const FormSecurity = forwardRef(({ register, setValue, error, theme = "dark" }, ref) => {
  const box = useRef(null);
  const widgetId = useRef(null);
  const [status, setStatus] = useState("loading");
  const honeypotId = useId();

  useEffect(() => {
    let cancelled = false;
    const setToken = (token) => setValue("recaptchaToken", token, { shouldValidate: !!token });

    loadRecaptcha()
      .then((grecaptcha) => {
        if (cancelled || !box.current || widgetId.current !== null) return;
        widgetId.current = grecaptcha.render(box.current, {
          sitekey: SITE_KEY,
          theme,
          callback: setToken,
          "expired-callback": () => setToken(""),
          "error-callback": () => setToken(""),
        });
        setStatus("ready");
      })
      .catch(() => !cancelled && setStatus("failed"));

    return () => {
      cancelled = true;
    };
  }, [setValue, theme]);

  useImperativeHandle(ref, () => ({
    reset() {
      setValue("recaptchaToken", "");
      if (widgetId.current !== null && window.grecaptcha) window.grecaptcha.reset(widgetId.current);
    },
  }));

  return (
    <div className={styles.wrap}>
      {/* Honeypot: hidden from people and assistive tech; bots fill it. */}
      <div className={styles.honeypot} aria-hidden="true">
        <label htmlFor={honeypotId}>Fax number</label>
        <input id={honeypotId} type="text" tabIndex={-1} autoComplete="off" {...register("faxNumber")} />
      </div>
      <input type="hidden" {...register("recaptchaToken")} />

      <div ref={box} className={styles.widget} />
      {status === "loading" && <p className={styles.note}>Loading security check…</p>}
      {status === "failed" && (
        <p className={styles.error} role="alert">
          The security check could not load. Check your connection or disable any content blocker, then reload the page.
        </p>
      )}
      {error && status === "ready" && (
        <p className={styles.error} role="alert">
          {error.message}
        </p>
      )}
    </div>
  );
});

export default FormSecurity;
